import React from 'react';
import { Link } from 'react-router-dom';
import {
  FilmIcon,
  MegaphoneIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  BanknotesIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useContent } from '../hooks/useContent';
import { useCampaigns } from '../hooks/useCampaigns';
import { useDashboardStats } from '../hooks/useDashboardStats';
import TopPerformingCampaigns from '../components/Dashboard/TopPerformingCampaigns';
import CampaignStatusBreakdown from '../components/Dashboard/CampaignStatusBreakdown';
// import PerformanceTrendChart from '../components/Dashboard/PerformanceTrendChart';
import { dashboardApi } from '../services/api';
import type { CampaignWidgetsResponse } from '../types/dashboard';

const Dashboard: React.FC = () => {
  // Campaign widgets state
  const [campaignWidgets, setCampaignWidgets] = React.useState<CampaignWidgetsResponse['data'] | null>(null);
  const [widgetsLoading, setWidgetsLoading] = React.useState(true);
  const [widgetsError, setWidgetsError] = React.useState<string | null>(null);

  // Use real data hooks
  const { stats, loading: statsLoading, error: statsError } = useDashboardStats();
  const { content, loading: contentLoading, error: contentError } = useContent({
    limit: 5,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  React.useEffect(() => {
    console.log('[Dashboard] Recent Content Props:', {
      data: content,
      count: content?.length,
      loading: contentLoading,
      error: contentError,
    });
  }, [content, contentLoading, contentError]);

  const { campaigns, loading: campaignsLoading, error: campaignsError } = useCampaigns({
    limit: 5,
    includeMetrics: true,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Fetch campaign widgets data
  React.useEffect(() => {
    const fetchCampaignWidgets = async () => {
      try {
        setWidgetsLoading(true);
        const response = await dashboardApi.getCampaignWidgets() as CampaignWidgetsResponse;
        setCampaignWidgets(response.data);
        setWidgetsError(null);
      } catch (error) {
        console.error('Failed to fetch campaign widgets:', error);
        setWidgetsError('Failed to load campaign performance data');
      } finally {
        setWidgetsLoading(false);
      }
    };

    fetchCampaignWidgets();
  }, []);

  // Format stats for display
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };


  // Impression ratio formatları
  const formatImpressionRatio = (current: number, target: number): string => {
    if (target === 0) return '0/0';
    return `${formatNumber(current)}/${formatNumber(target)}`;
  };

  const formatPercentage = (ratio: number): string => {
    return `${ratio.toFixed(1)}%`;
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getContentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      movie: 'Film',
      series: 'Dizi',
      documentary: 'Belgesel',
      live: 'Canlı'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      active: 'Aktif',
      paused: 'Duraklatıldı',
      draft: 'Taslak',
      completed: 'Tamamlandı',
      ready: 'Hazır',
      processing: 'İşleniyor',
      pending: 'Bekliyor',
      failed: 'Başarısız',
      archived: 'Arşivlendi'
    };
    return labels[status] || status;
  };

  // Create stats array from real data
  const dashboardStats = stats ? [
    {
      name: 'Toplam İçerik',
      value: formatNumber(stats.totalContent),
      change: '+12%', // TODO: Calculate from historical data
      changeType: 'increase' as const,
      icon: FilmIcon,
    },
    {
      name: 'Aktif Kampanyalar',
      value: formatNumber(stats.activeCampaigns),
      change: '+3', // TODO: Calculate from historical data
      changeType: 'increase' as const,
      icon: MegaphoneIcon,
    },
    {
      name: 'Toplam Görüntülenme',
      value: formatNumber(stats.totalImpressions),
      change: '+18%', // TODO: Calculate from historical data
      changeType: 'increase' as const,
      icon: EyeIcon,
    },
    {
      name: 'Tıklama Oranı',
      value: `${(stats.averageCTR * 100).toFixed(1)}%`,
      change: '+0.5%', // TODO: Calculate from historical data
      changeType: 'increase' as const,
      icon: CursorArrowRaysIcon,
    },
    {
      name: 'Harcama Oranı',
      value: stats.spendRatio ? formatPercentage(stats.spendRatio) : formatPercentage((stats.totalBudget || 0) > 0 ? (stats.totalSpent / (stats.totalBudget || 1)) * 100 : 0),
      change: '+25%', // TODO: Calculate from historical data
      changeType: 'increase' as const,
      icon: BanknotesIcon,
    },
  ] : [];

  // Loading component
  const LoadingCard = () => (
    <div className="animate-pulse">
      <div className="bg-gray-200 h-4 rounded mb-2"></div>
      <div className="bg-gray-200 h-8 rounded"></div>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message }: { message: string }) => (
    <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
      <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Platformunuzun reklam performansına genel bakış
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {statsLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="bg-white px-4 py-5 shadow rounded-lg">
              <LoadingCard />
            </div>
          ))
        ) : statsError ? (
          <div className="col-span-full">
            <ErrorMessage message={statsError} />
          </div>
        ) : (
          dashboardStats.map((stat) => (
            <div
              key={stat.name}
              className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6"
            >
              <dt>
                <div className="absolute rounded-md bg-blue-500 p-3">
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500">
                  {stat.name}
                </p>
              </dt>
              <dd className="ml-16 flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                <p
                  className={`ml-2 flex items-baseline text-sm font-semibold ${
                    stat.changeType === 'increase'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {stat.change}
                </p>
              </dd>
            </div>
          ))
        )}
      </div>

      {/* Content and Campaigns Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Content */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Son İçerikler</h3>
              <a
                href="/content"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Tümünü Gör
              </a>
            </div>
            <div className="mt-6 flow-root">
              {contentLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <LoadingCard key={index} />
                  ))}
                </div>
              ) : contentError ? (
                <ErrorMessage message={contentError} />
              ) : content.length === 0 ? (
                <div className="text-center py-8">
                  <FilmIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Henüz içerik bulunmuyor</p>
                </div>
              ) : (
                <ul className="-my-5 divide-y divide-gray-200">
                  {content.map((contentItem) => (
                    <li key={contentItem.id} className="py-4">
                      <Link
                        to={`/content/${contentItem.id}`}
                        className="flex items-center space-x-4 hover:bg-gray-50 -mx-6 px-6 py-4 rounded-lg transition-colors duration-200"
                      >
                        <div className="flex-shrink-0">
                          <FilmIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {contentItem.title}
                          </p>
                          <p className="truncate text-sm text-gray-500">
                            {getContentTypeLabel(contentItem.contentType)} • {formatDuration(contentItem.durationSeconds)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contentItem.status === 'ready'
                                ? 'bg-green-100 text-green-800'
                                : contentItem.status === 'processing'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {getStatusLabel(contentItem.status)}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Recent Campaigns */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Son Kampanyalar</h3>
              <a
                href="/campaigns"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Tümünü Gör
              </a>
            </div>
            <div className="mt-6 flow-root">
              {campaignsLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <LoadingCard key={index} />
                  ))}
                </div>
              ) : campaignsError ? (
                <ErrorMessage message={campaignsError} />
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <MegaphoneIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Henüz kampanya bulunmuyor</p>
                </div>
              ) : (
                <ul className="-my-5 divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <li key={campaign.id} className="py-4">
                      <Link
                        to={`/campaigns/${campaign.id}`}
                        className="flex items-center space-x-4 hover:bg-gray-50 -mx-6 px-6 py-4 rounded-lg transition-colors duration-200"
                      >
                        <div className="flex-shrink-0">
                          <MegaphoneIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {campaign.name}
                          </p>
                          <p className="truncate text-sm text-gray-500">
                            {/* Backend'den gerçek impression/click verileri henüz gelmiyor, placeholder görünüm */}
                            {campaign.impressions || 0} gösterim • {campaign.clicks || 0} tıklama
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {/* Harcama artık impression oranı olarak gösteriliyor */}
                            {campaign.spent && campaign.budget
                              ? formatImpressionRatio(campaign.spent, campaign.budget)
                              : '0/0'}
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              campaign.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : campaign.status === 'paused'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {getStatusLabel(campaign.status || 'draft')}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Performance Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Campaigns */}
        <TopPerformingCampaigns
          campaigns={campaignWidgets?.topPerformingCampaigns || []}
          loading={widgetsLoading}
          error={widgetsError || undefined}
        />
        
        {/* Campaign Status Breakdown */}
        <CampaignStatusBreakdown
          data={campaignWidgets?.campaignStatusBreakdown || { active: 0, paused: 0, draft: 0, completed: 0 }}
          loading={widgetsLoading}
          error={widgetsError || undefined}
        />
      </div>

      {/* Performance Trend Chart - Temporarily disabled */}
      {/* <PerformanceTrendChart
        data={campaignWidgets?.performanceTrends || []}
        loading={widgetsLoading}
        error={widgetsError || undefined}
      /> */}


    </div>
  );
};

export default Dashboard;